/**
 * Copyright © Veeam Software Group GmbH.
 */

import React, { useEffect } from 'react';
import { usePageWizardStore, LinkButton, Text, useGlobalLang, useGlobalServices } from '@veeam-vspc/shared/components';
import { formatStr } from '@veeam-vspc/shared/helpers';

import type { WizardStep, WizardStepData } from '@veeam-vspc/shared/components';
import type { VspcLang } from 'configs/languages';

import { RadioGroup } from 'components/controls/RadioGroup';
import { StepLayout } from 'components/layouts/StepLayout';
import { ManagedCompaniesMode } from 'views/pages/RolesAndUsersPage/components/AdminUser/enums/managed-companies-mode';
import { UserRoles } from 'core/enums';
import { ManagedCompaniesPanel } from './components/ManagedCompaniesPanel';

import type { SSORuleModel } from 'views/pages/RolesAndUsersPage/components/Base/interfaces/sso-rule-model';

const Companies = (wizardStepData: WizardStepData<SSORuleModel>) => {
    const lang = useGlobalLang<VspcLang>();
    const { notificationService } = useGlobalServices();
    const { data, onDataChange, validationState } = wizardStepData;
    const selectedCompaniesCount = data.selectedCompanies?.length ?? 0;
    const wizardContext = usePageWizardStore<SSORuleModel>();
    const showPanel = () => wizardContext.openPanel(hide => <ManagedCompaniesPanel {...wizardStepData} hidePanel={hide} />);

    useEffect(() => {
        if (validationState.isForce()) {
            validationState.markAsHandledForce();

            if (data.selectAllCompanies === ManagedCompaniesMode.CustomSelection && data.selectedCompanies.length === 0) {
                showPanel();
            }

            if (data.selectAllCompanies === ManagedCompaniesMode.CustomSelection &&
                data.selectedCompanies?.some(company => company.managedByReseller)
            ) {
                const companyNames = data.selectedCompanies
                    .filter(company => company.managedByReseller)
                    .map(company => company.name)
                    .join(', ');

                notificationService.error(lang.ERROR, [formatStr(lang.CANNOT_CREATE_A_NEW_AUTHORIZATION, companyNames)]);
            }
        }
    });

    return (
        <StepLayout
            title={lang.COMPANIES}
            description={lang.SELECT_COMPANIES_ACCESSIBLE_FOR_THIS_USER}
        >
            <RadioGroup
                value={data.selectAllCompanies}
                onChange={(value: ManagedCompaniesMode) => {
                    data.selectAllCompanies = value;

                    onDataChange(data);
                }}
                buttons={[
                    {
                        label: lang.ALL_COMPANIES_INCLUDING_NEWLY_ADDED,
                        value: ManagedCompaniesMode.AllCompanies,
                    },
                    {
                        render: () => (
                            <>
                                <Text>
                                    {`${lang.SELECTED_COMPANIES_ONLY} ${selectedCompaniesCount > 0 ? `(${selectedCompaniesCount})` : ''}`}
                                </Text>

                                <LinkButton
                                    onClick={showPanel}
                                    disabled={data.selectAllCompanies === ManagedCompaniesMode.AllCompanies}
                                >
                                    {`(${lang.CONFIGURE})`}
                                </LinkButton>
                            </>
                        ),
                        value: ManagedCompaniesMode.CustomSelection,
                    },
                ]}
            />
        </StepLayout>
    );
};

export const getCompaniesStep = (title: string): WizardStep<SSORuleModel> => ({
    title,
    render: data => <Companies {...data} />,
    validate({ data }) {
        if (data.selectAllCompanies === ManagedCompaniesMode.CustomSelection && data.selectedCompanies.length === 0) {
            return false;
        }

        if (data.selectAllCompanies === ManagedCompaniesMode.CustomSelection && data.selectedCompanies?.some(company => company.managedByReseller)) {
            return false;
        }

        return true;
    },
    isHidden: ({ data }) => data.model.role === UserRoles.PortalAdministrator,
});
